import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import UsageWidget from "../components/UsageWidget";
import UpgradeNudge from "../components/UpgradeNudge";
import { useLanguage } from "../contexts/LanguageContext";
import OnboardingTour from "../components/OnboardingTour";
import {
  Zap,
  Briefcase,
  FileText,
  MessageSquare,
  Users,
  LogOut,
  Plus,
  ChevronRight,
  MapPin,
  Clock,
  Eye,
  Sparkles,
  Search,
  BarChart3,
  Building2,
  Award,
  Share2,
  Flame,
  TrendingUp,
  Edit,
  Calendar,
  UserCheck,
  UserX,
  Star,
  CheckCircle,
  XCircle,
  Video,
  User
} from "lucide-react";

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchUnreadCount();
    fetchLeaderboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/dashboard/recruiter`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/messages/unread/count`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API}/analytics/share-leaderboard`, { credentials: "include" });
      if (res.ok) setLeaderboard(await res.json());
    } catch (e) {
      console.error("Failed to fetch share leaderboard:", e);
    }
  };

  const handleUpdateStatus = async (jobId, applicationId, newStatus) => {
    setActionLoading(applicationId);
    try {
      const response = await fetch(`${API}/jobs/${jobId}/applications/${applicationId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        toast.success(`Applicant ${newStatus === "shortlisted" ? "shortlisted" : "rejected"}!`);
        fetchDashboard(); // Refresh data
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update applicant status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "text-yellow-400 bg-yellow-500/10",
      reviewed: "text-blue-400 bg-blue-500/10",
      shortlisted: "text-green-400 bg-green-500/10",
      interviewed: "text-purple-400 bg-purple-500/10",
      offered: "text-emerald-400 bg-emerald-500/10",
      hired: "text-green-500 bg-green-500/20",
      rejected: "text-red-400 bg-red-500/10"
    };
    return colors[status] || "text-gray-400 bg-gray-500/10";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Onboarding Tour */}
      <OnboardingTour userRole="recruiter" />
      
      {/* Sidebar */}
      <Sidebar unreadCount={unreadCount} />

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8" data-testid="recruiter-dashboard">
        {/* Upgrade Nudge */}
        <UpgradeNudge />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">
              {t("welcomeBack")}, <span className="text-crimson">{user?.name?.split(' ')[0]}</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">{t("hiringPipeline")}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/jobs/create")}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider text-sm"
              data-testid="create-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Post New Job
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="stat-value">{dashboardData?.stats?.active_jobs || 0}</div>
            <div className="stat-label">Active Jobs</div>
          </motion.div>
          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="stat-value">{dashboardData?.stats?.total_applicants || 0}</div>
            <div className="stat-label">Total Applicants</div>
          </motion.div>
          <motion.div 
            className="stat-card cursor-pointer hover:border-yellow-500/50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => navigate("/applicant-pipeline?status=pending")}
          >
            <div className="stat-value text-yellow-400">{dashboardData?.stats?.pending_review || 0}</div>
            <div className="stat-label">Pending Review</div>
          </motion.div>
          <motion.div 
            className="stat-card cursor-pointer hover:border-green-500/50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            onClick={() => navigate("/applicant-pipeline?status=shortlisted")}
          >
            <div className="stat-value text-green-400">{dashboardData?.stats?.shortlisted || 0}</div>
            <div className="stat-label">Shortlisted</div>
          </motion.div>
          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="stat-value">{dashboardData?.stats?.pending_interviews || 0}</div>
            <div className="stat-label">Pending Interviews</div>
          </motion.div>
          <motion.div 
            className="stat-card cursor-pointer hover:border-emerald-500/50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            onClick={() => navigate("/applicant-pipeline?status=hired")}
          >
            <div className="stat-value text-emerald-400">{dashboardData?.stats?.hired || 0}</div>
            <div className="stat-label">Hired</div>
          </motion.div>
        </div>

        {/* Quick Actions Panel - Pending Applicants */}
        {dashboardData?.pending_applicants?.length > 0 && (
          <motion.div
            className="mb-8 bg-obsidian-light border border-yellow-500/30 p-4 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="font-heading text-base sm:text-lg font-bold">APPLICANTS AWAITING REVIEW</h2>
                  <p className="text-gray-400 text-xs sm:text-sm">Take quick action on pending applications</p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/applicant-pipeline")}
                variant="outline"
                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 text-xs sm:text-sm"
                data-testid="view-all-applicants-btn"
              >
                View Pipeline <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
              </Button>
            </div>

            <div className="space-y-3">
              {dashboardData.pending_applicants.slice(0, 5).map((applicant, index) => (
                <motion.div
                  key={applicant.application_id || index}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 bg-obsidian border border-[#333] hover:border-[#444] transition-colors"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`pending-applicant-${applicant.application_id}`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {applicant.candidate_picture ? (
                        <img src={applicant.candidate_picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-white text-sm sm:text-base truncate">{applicant.candidate_name}</h4>
                      <p className="text-xs sm:text-sm text-gray-400 truncate">{applicant.candidate_headline || "Job Seeker"}</p>
                      <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1 sm:gap-2 mt-1">
                        <Briefcase className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                        <span className="truncate">Applied for: <span className="text-crimson">{applicant.job_title}</span></span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                    {applicant.match_score > 0 && (
                      <div className="text-center">
                        <div className={`text-base sm:text-lg font-bold ${
                          applicant.match_score >= 70 ? "text-green-400" :
                          applicant.match_score >= 40 ? "text-yellow-400" : "text-gray-400"
                        }`}>
                          {applicant.match_score}%
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-500">Match</div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Button
                        onClick={() => navigate(`/candidates/${applicant.candidate_id}`)}
                        variant="outline"
                        size="sm"
                        className="border-[#333] text-white hover:border-crimson h-8 w-8 sm:h-9 sm:w-9 p-0"
                        data-testid={`view-candidate-${applicant.application_id}`}
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus(applicant.job_id, applicant.application_id, "shortlisted")}
                        disabled={actionLoading === applicant.application_id}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white h-8 w-8 sm:h-9 sm:w-9 p-0"
                        data-testid={`shortlist-${applicant.application_id}`}
                      >
                        {actionLoading === applicant.application_id ? (
                          <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus(applicant.job_id, applicant.application_id, "rejected")}
                        disabled={actionLoading === applicant.application_id}
                        variant="outline"
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8 sm:h-9 sm:w-9 p-0"
                        data-testid={`reject-${applicant.application_id}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {dashboardData.pending_applicants.length > 5 && (
              <div className="mt-4 text-center">
                <Button
                  onClick={() => navigate("/applicant-pipeline?status=pending")}
                  variant="ghost"
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  View all {dashboardData.stats?.pending_review} pending applicants
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* Hiring Pipeline Overview */}
        {dashboardData?.status_breakdown && (
          <motion.div
            className="mb-8 bg-obsidian-light border border-[#222] p-4 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4">
              <h2 className="font-heading text-base sm:text-lg font-bold">HIRING PIPELINE</h2>
              <Button
                onClick={() => navigate("/applicant-pipeline")}
                variant="ghost"
                className="text-crimson hover:text-crimson-hover text-xs sm:text-sm"
              >
                Full Pipeline <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex lg:items-center gap-2 overflow-x-auto pb-2">
              {[
                { key: "pending", label: "Pending", icon: Clock },
                { key: "reviewed", label: "Reviewed", icon: Eye },
                { key: "shortlisted", label: "Shortlisted", icon: Star },
                { key: "interviewed", label: "Interviewed", icon: Video },
                { key: "offered", label: "Offered", icon: Award },
                { key: "hired", label: "Hired", icon: CheckCircle },
                { key: "rejected", label: "Rejected", icon: XCircle },
              ].map((stage, index) => (
                <button
                  key={stage.key}
                  onClick={() => navigate(`/applicant-pipeline?status=${stage.key}`)}
                  className={`flex-shrink-0 flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 border transition-all hover:border-crimson/50 ${getStatusColor(stage.key)} border-current/30 text-xs sm:text-sm`}
                  data-testid={`pipeline-${stage.key}`}
                >
                  <stage.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">{dashboardData.status_breakdown[stage.key] || 0}</span>
                  <span className="hidden sm:inline text-xs opacity-70">{stage.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Subscription Info & Usage Widget */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
          {/* Subscription Info - Takes 2 columns */}
          {dashboardData?.subscription && (
            <motion.div 
              className="lg:col-span-2 bg-gradient-to-r from-crimson/10 to-transparent border border-crimson/30 p-4 sm:p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-base sm:text-lg font-bold">
                      {dashboardData.subscription.name || "Free Plan"}
                    </h3>
                    <p className="text-gray-400 text-xs sm:text-sm truncate">
                      {dashboardData.subscription.features?.slice(0, 2).join(" • ") || "Upgrade to unlock more features"}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate("/settings")}
                  variant="outline"
                  className="border-crimson text-crimson hover:bg-crimson hover:text-white uppercase tracking-wider text-xs sm:text-sm w-full sm:w-auto"
                  data-testid="upgrade-btn"
                >
                  Manage Plan
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* Usage Widget - Takes 1 column */}
          <div className={dashboardData?.subscription ? "" : "lg:col-span-3"}>
            <UsageWidget />
          </div>
        </div>

        {/* Recent Jobs */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="font-heading text-xl sm:text-2xl font-bold">YOUR JOBS</h2>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                onClick={() => navigate("/jobs/manage")}
                variant="ghost"
                className="text-crimson hover:text-crimson-hover text-xs sm:text-sm uppercase tracking-widest"
              >
                View All <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
              </Button>
              <Button
                onClick={() => navigate("/jobs/create")}
                className="bg-crimson hover:bg-crimson-hover text-white text-xs sm:text-sm"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> New Job
              </Button>
            </div>
          </div>

          {dashboardData?.recent_jobs?.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.recent_jobs.map((job, index) => (
                <motion.div
                  key={job.job_id}
                  className={`bg-obsidian-light border p-4 sm:p-6 hover:border-crimson/50 transition-all card-hover relative ${
                    job.promoted ? "border-crimson/50" : "border-[#222]"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {/* Promoted Badge */}
                  {job.promoted && (
                    <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-crimson px-2 py-1 flex items-center gap-1">
                      <Flame className="w-3 h-3 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-bold">Promoted</span>
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    <div className="flex-1 min-w-0 pr-0 sm:pr-20">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className="font-heading text-lg sm:text-xl font-bold break-words">{job.title}</h3>
                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                          job.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate">{job.industry}</span>
                        </span>
                        {job.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span className="truncate">{job.location}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          {job.employment_type}
                        </span>
                      </div>
                      {job.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {job.skills.slice(0, 4).map((skill, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#1a1a1a] text-xs text-gray-300">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions Row */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 border-t border-[#333]">
                      <div className="text-center sm:text-left">
                        <div className="font-heading text-xl sm:text-2xl font-bold text-crimson inline sm:block">
                          {job.applicants?.length || 0}
                        </div>
                        <span className="text-xs text-gray-500 uppercase ml-1 sm:ml-0"> Applicants</span>
                      </div>
                      <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
                        <Button
                          onClick={() => navigate(`/jobs/${job.job_id}`)}
                          variant="outline"
                          size="sm"
                          className="border-[#333] hover:border-crimson text-white uppercase tracking-wider text-xs flex-1 sm:flex-none"
                          data-testid={`view-job-${job.job_id}`}
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> View
                        </Button>
                        <Button
                          onClick={() => navigate(`/jobs/${job.job_id}/edit`)}
                          variant="outline"
                          size="sm"
                          className="border-[#333] hover:border-crimson text-white uppercase tracking-wider text-xs flex-1 sm:flex-none"
                          data-testid={`edit-job-${job.job_id}`}
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          onClick={() => navigate(`/shortlist/${job.job_id}`)}
                          size="sm"
                          className="bg-crimson/20 hover:bg-crimson text-crimson hover:text-white uppercase tracking-wider text-xs flex-1 sm:flex-none"
                          data-testid={`shortlist-job-${job.job_id}`}
                        >
                          <Zap className="w-3 h-3 mr-1" /> AI Shortlist
                        </Button>
                        {!job.promoted && (
                          <Button
                            onClick={() => navigate(`/addons?job_id=${job.job_id}`)}
                            variant="outline"
                            size="sm"
                            className="border-crimson/50 text-crimson hover:bg-crimson hover:text-white uppercase tracking-wider text-xs flex-1 sm:flex-none"
                            data-testid={`promote-job-${job.job_id}`}
                          >
                            <Flame className="w-3 h-3 mr-1" /> Promote
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Jobs Posted Yet</h3>
              <p className="text-gray-400 mb-6">Start hiring by posting your first job</p>
              <Button
                onClick={() => navigate("/jobs/create")}
                className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                data-testid="first-job-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Post Your First Job
              </Button>
            </div>
          )}
        </div>

        {/* Share Leaderboard */}
        {leaderboard && leaderboard.leaderboard?.length > 0 && (
          <div className="mb-8" data-testid="share-leaderboard">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-crimson" />
                <h2 className="font-heading text-xl sm:text-2xl font-bold">SHARE <span className="text-crimson">LEADERBOARD</span></h2>
              </div>
              <Button
                onClick={() => navigate("/analytics/recruiter")}
                variant="ghost"
                className="text-crimson hover:text-crimson-hover text-xs uppercase tracking-widest"
              >
                Full Analytics <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>

            {/* Summary stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-obsidian-light border border-[#222] p-4 text-center">
                <div className="text-2xl font-bold text-white">{leaderboard.total_shares}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Total Shares</div>
              </div>
              <div className="bg-obsidian-light border border-[#222] p-4 text-center">
                <div className="text-2xl font-bold text-crimson">{leaderboard.avg_shares}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Avg / Job</div>
              </div>
              <div className="bg-obsidian-light border border-[#222] p-4 text-center hidden sm:block">
                <div className="text-2xl font-bold text-white">{leaderboard.leaderboard.length}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Active Jobs</div>
              </div>
            </div>

            {/* Leaderboard list */}
            <div className="bg-obsidian-light border border-[#222]">
              {leaderboard.leaderboard.slice(0, 5).map((job, i) => {
                const isUnderperforming = job.total_shares < leaderboard.avg_shares && !job.promoted;
                const maxShares = leaderboard.leaderboard[0]?.total_shares || 1;
                const barWidth = maxShares > 0 ? Math.max((job.total_shares / maxShares) * 100, 2) : 2;

                return (
                  <div
                    key={job.job_id}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-[#1a1a1a] last:border-0 ${isUnderperforming ? "bg-crimson/5" : ""}`}
                    data-testid={`leaderboard-job-${job.job_id}`}
                  >
                    {/* Rank */}
                    <div className={`w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0 ${
                      i === 0 ? "bg-crimson text-white" : i < 3 ? "bg-[#222] text-white" : "text-gray-500"
                    }`}>
                      {i + 1}
                    </div>

                    {/* Job info + bar */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-white font-medium truncate">{job.title}</span>
                        {job.promoted && (
                          <span className="bg-crimson/20 text-crimson text-[10px] px-1.5 py-0.5 uppercase tracking-wider font-bold shrink-0">Promoted</span>
                        )}
                      </div>
                      {/* Mini share bar */}
                      <div className="h-1.5 bg-[#1a1a1a] w-full">
                        <div className="h-full bg-crimson transition-all" style={{ width: `${barWidth}%` }} />
                      </div>
                      {/* Platform mini icons */}
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                        {job.by_platform?.twitter > 0 && <span>X: {job.by_platform.twitter}</span>}
                        {job.by_platform?.linkedin > 0 && <span>LI: {job.by_platform.linkedin}</span>}
                        {job.by_platform?.facebook > 0 && <span>FB: {job.by_platform.facebook}</span>}
                        {job.by_platform?.whatsapp > 0 && <span>WA: {job.by_platform.whatsapp}</span>}
                        {job.by_platform?.email > 0 && <span>Email: {job.by_platform.email}</span>}
                        {job.by_platform?.copy_link > 0 && <span>Link: {job.by_platform.copy_link}</span>}
                      </div>
                    </div>

                    {/* Share count + boost */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">{job.total_shares}</div>
                        <div className="text-[10px] text-gray-500 uppercase">shares</div>
                      </div>
                      {isUnderperforming && (
                        <Button
                          onClick={() => navigate(`/addons?job_id=${job.job_id}`)}
                          size="sm"
                          className="bg-crimson hover:bg-crimson/80 text-white text-[10px] uppercase tracking-wider px-2 py-1 h-auto"
                          data-testid={`boost-job-${job.job_id}`}
                        >
                          <Flame className="w-3 h-3 mr-0.5" /> Boost
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <button
            onClick={() => navigate("/jobs/create")}
            className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-all text-left group"
            data-testid="quick-action-post"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center group-hover:bg-crimson/20 transition-colors flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base sm:text-lg font-bold">AI Job Creator</h3>
              <p className="text-gray-400 text-xs sm:text-sm truncate">Generate ATS-optimized descriptions</p>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 group-hover:text-crimson transition-colors flex-shrink-0" />
          </button>

          <button
            onClick={() => navigate("/candidates")}
            className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-all text-left group"
            data-testid="quick-action-search"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center group-hover:bg-crimson/20 transition-colors flex-shrink-0">
              <Search className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base sm:text-lg font-bold">Find Candidates</h3>
              <p className="text-gray-400 text-xs sm:text-sm truncate">Search talent database</p>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 group-hover:text-crimson transition-colors flex-shrink-0" />
          </button>

          <button
            onClick={() => navigate("/community")}
            className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-all text-left group sm:col-span-2 lg:col-span-1"
            data-testid="quick-action-community"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center group-hover:bg-crimson/20 transition-colors flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base sm:text-lg font-bold">Community</h3>
              <p className="text-gray-400 text-xs sm:text-sm truncate">Share insights & resources</p>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 group-hover:text-crimson transition-colors flex-shrink-0" />
          </button>
        </div>
      </main>
    </div>
  );
};

export default RecruiterDashboard;
